import React from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useRecoilValue } from 'recoil';

import { hourlyWeatherForecastIDS, hourlyWeatherPageTestIDs, myWeatherPageTestIDs, Theme } from '@constants';
import { useSegmentTrackWeatherForecast } from '@hooks';
import { navigationService } from '@services';
import { dayDateTimeState, hourlyForecastState } from '@store';

import { hourlyForecastType } from '../../../constants/types/api/hourlyForecastType';
import { roundNearest5 } from '../../../helpers/DataFormatting';
import { isDisplayable, missingValue } from '../../../helpers/StringUtils';
import { TimeUtils } from '../../../helpers/TimeUtils';
import { TWCTile } from '../../focusable/TWCTile/TWCTile';
import { WeatherTileDetail } from '../../myWeather/WeatherTileDetail/WeatherTileDetail';
import { TWCText } from '../../primitives/TWCText/TWCText';
/**
 * Formats hourly data for the component. The time is converted to the correct format. Tempurature values
 * will be converted to strings with degrees. Prcip values will be rounded to nearest 5 and converted to strings
 * with percent.
 * @param {*} data Raw data from 2 day hourly forecast
 * @param {*} timeFormat Time formatting to use, either 12h or 24h. Default is 12h
 * @returns Returns formatted hourly data for next three hours.
 */
const getHourly = (data, timeZone, timeFormat = '12h') => {
  if (!data) {
    return;
  }

  const { iconCodeExtend, precipChance, temperature, validTimeUtc, wxPhraseLong } = data;

  return Array.from({ length: 3 }, (_, index) => ({
    time: validTimeUtc[index]
      ? TimeUtils.getZonedHourTimeAMPM(TimeUtils.dateFromEpoch(validTimeUtc[index]), timeZone, timeFormat)
      : missingValue,
    title: validTimeUtc[index]
      ? TimeUtils.getZonedHourTime(TimeUtils.dateFromEpoch(validTimeUtc[index]), timeZone, timeFormat)
      : missingValue,
    iconCodeExtend: iconCodeExtend[index],
    rawTemperature: isDisplayable(temperature[index]) ? `${temperature[index]}` : missingValue,
    temperature: isDisplayable(temperature[index]) ? `${temperature[index]}°` : missingValue,
    weather: isDisplayable(wxPhraseLong[index]) ? wxPhraseLong[index] : missingValue,
    rawprecipChance: isDisplayable(precipChance[index]) ? `${roundNearest5(precipChance[index])}` : missingValue,
    precipChance: isDisplayable(precipChance[index]) ? `${roundNearest5(precipChance[index])}%` : missingValue,
  }));
};

const HourlyWeatherForecastTileAPI = ({ nextFocusDown, nextFocusLeft, nextFocusRight, nextFocusUp }) => {
  const { data, error, isLoading } = useRecoilValue(hourlyForecastState);

  return (
    <HourlyWeatherForecastTileUI
      data={data}
      error={error}
      isLoading={isLoading}
      nextFocusDown={nextFocusDown}
      nextFocusLeft={nextFocusLeft}
      nextFocusRight={nextFocusRight}
      nextFocusUp={nextFocusUp}
    />
  );
};

const tPath = 'hourlyWeatherForecastTile';

const HourlyWeatherForecastTileUI = ({
  data,
  error,
  isLoading,
  nextFocusDown,
  nextFocusLeft,
  nextFocusRight,
  nextFocusUp,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: tPath,
  });

  const { timeZone } = useRecoilValue(dayDateTimeState);

  const hourlyData = data ? getHourly(data, timeZone) : undefined;

  if (!hourlyData) {
    error = 'Hourly data failed to parse.';
  }

  const [forecastType, setSegmentTrackWeatherForecast] = useSegmentTrackWeatherForecast();

  const handleOnPress = () => {
    setSegmentTrackWeatherForecast(forecastType.HOURLY);
    navigationService.navigate(hourlyWeatherPageTestIDs.hourlyWeatherPage);
  };

  const hour1Label = error
    ? ''
    : t('tileHourAccessibilityLabel', {
        time: hourlyData?.[0].time,
        weather: hourlyData?.[0].weather,
        temperature: hourlyData?.[0].rawTemperature,
        precipitation: hourlyData?.[0].rawprecipChance,
      }).replace(missingValue, 'unknown');

  const hour2Label = error
    ? ''
    : t('tileHourAccessibilityLabel', {
        time: hourlyData?.[1].time,
        weather: hourlyData?.[1].weather,
        temperature: hourlyData?.[1].rawTemperature,
        precipitation: hourlyData?.[1].rawprecipChance,
      }).replace(missingValue, 'unknown');

  const hour3Label = error
    ? ''
    : t('tileHourAccessibilityLabel', {
        time: hourlyData?.[2].time,
        weather: hourlyData?.[2].weather,
        temperature: hourlyData?.[2].rawTemperature,
        precipitation: hourlyData?.[2].rawprecipChance,
      }).replace(missingValue, 'unknown');

  return (
    <TWCTile
      accessibilityHint={t('tileAccessibilityHint')}
      accessibilityLabel={
        error
          ? t('errorAccessibilityLabel')
          : t('tileAccessibilityLabel', {
              hour1: hour1Label,
              hour2: hour2Label,
              hour3: hour3Label,
            })
      }
      // hasTVPreferredFocus={isFocused}
      height={198}
      nextFocusDown={nextFocusDown}
      nextFocusLeft={nextFocusLeft}
      nextFocusRight={nextFocusRight}
      nextFocusUp={nextFocusUp}
      testID={myWeatherPageTestIDs.hourlyForecastTile}
      width={516}
      onPress={handleOnPress}
    >
      <View style={styles.weatherTileDetailHeaderWrapper}>
        <TWCText testID={hourlyWeatherForecastIDS.headerText} variant={'regularText24Transparent'}>
          {t('headerText')}
        </TWCText>
      </View>
      <View style={styles.weatherTileDetailContentWrapper}>
        {isLoading ? (
          <ActivityIndicator color={Theme.loading} size={'large'} />
        ) : (
          <View style={styles.weatherTileDetailTimePeriodWrapper}>
            <WeatherTileDetail data={hourlyData?.[0]} error={error} testID={hourlyWeatherForecastIDS.firstHour} />
            <WeatherTileDetail data={hourlyData?.[1]} error={error} testID={hourlyWeatherForecastIDS.secondHour} />
            <WeatherTileDetail data={hourlyData?.[2]} error={error} testID={hourlyWeatherForecastIDS.thirdHour} />
          </View>
        )}
      </View>
    </TWCTile>
  );
};

HourlyWeatherForecastTileUI.propTypes = {
  data: PropTypes.object,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
};

HourlyWeatherForecastTileUI.defaultProps = {
  data: null,
  error: null,
  isLoading: false,
  nextFocusDown: null,
  nextFocusLeft: null,
  nextFocusRight: null,
  nextFocusUp: null,
};

export const HourlyWeatherForecastTile = {
  API: HourlyWeatherForecastTileAPI,
  UI: HourlyWeatherForecastTileUI,
};

HourlyWeatherForecastTileUI.propTypes = {
  data: hourlyForecastType,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
};

HourlyWeatherForecastTileUI.defaultProps = {
  data: null,
  error: null,
  isLoading: false,
  nextFocusDown: null,
  nextFocusLeft: null,
  nextFocusRight: null,
  nextFocusUp: null,
};

const styles = StyleSheet.create({
  weatherTileDetailContentWrapper: {
    borderBottomLeftRadius: Theme.tileBorderRadius,
    borderBottomRightRadius: Theme.tileBorderRadius,
    elevation: 500,
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  weatherTileDetailHeaderWrapper: {
    paddingLeft: Theme.px(30),
    paddingVertical: Theme.px(7),
  },
  weatherTileDetailTimePeriodWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: Theme.px(516),
  },
});
